import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames/bind';

import Img from '../../../Img';
import Link from '../../../Link';
import Box from '../../../../shared/Box';
import Text from '../../../../shared/Text/index.tsx';
import Pressable from '../../../../shared/Pressable';
import IconBadge from '../../../IconBadge';

import { clickedNavLinkGoogleAnalytics } from '../../events';

import styles from './styles.sass';
import LinkStyles from '../../LinkStyles.sass';

const cx = classnames.bind(styles);
const cxLinkStyles = classnames.bind(LinkStyles);

const getMobileAccountLinks = (additionalLinks = [], withDistribution) => [
  { label: 'Update settings', to: '/dashboard/podcast/edit' },
  ...(withDistribution
    ? [
        {
          label: 'Distribution',
          to: '/dashboard/podcast/distribution',
        },
      ]
    : []),
  {
    label: 'Help',
    target: '_blank',
    href: 'https://help.anchor.fm/',
  },
  ...additionalLinks,
];

const MobileNav = ({
  links,
  isSignedIn,
  onLogOut,
  onClickClose,
  pathname,
  isShowingDistribution,
}) => (
  <div className={styles.container}>
    <div className={styles.contentWrapper}>
      <div className={styles.topSection}>
        <a
          href="/"
          className={styles.iconLink}
          onClick={() => clickedNavLinkGoogleAnalytics('Home')}
        >
          <Img
            withRetina
            src="https://d12xoj7p9moygp.cloudfront.net/images/anchor-logo-header.png"
            alt="Anchor logo"
          />
        </a>
        <button
          className={styles.closeButton}
          on="tap:sidebar.close"
          type="button"
          tabIndex="0"
          onClick={onClickClose}
        >
          <span className={`${styles.closeLine} ${styles.closeLineOne}`} />
          <span className={`${styles.closeLine} ${styles.closeLineTwo}`} />
        </button>
      </div>
      <div className={styles.content}>
        <ul className={styles.linkList}>
          {links.map(link => {
            const {
              label,
              href,
              to,
              isShowingNotificationBadge,
              notificationBadge,
            } = link;
            return (
              <li key={label} className={styles.linkListItem}>
                <Link
                  className={`${cxLinkStyles({
                    link: true,
                    linkActive: pathname === (to || href),
                  })} ${styles.link}`}
                  onClick={() => {
                    onClickClose();
                    clickedNavLinkGoogleAnalytics(label);
                  }}
                  href={href}
                  to={to}
                >
                  <span>
                    {label}
                    {isShowingNotificationBadge && (
                      <Box
                        position="absolute"
                        top
                        right
                        marginTop={0}
                        marginRight={-10}
                      >
                        <IconBadge
                          backgroundColor="green"
                          iconColor="#292F36"
                          width={10}
                          type={notificationBadge}
                        />
                      </Box>
                    )}
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
        <hr className={styles.rule} />
      </div>
      <div className={styles.accountLinksWrapper}>
        {isSignedIn ? (
          <ul className={styles.linkList}>
            {getMobileAccountLinks(
              [
                {
                  label: 'Log out',
                  onClick: onLogOut,
                },
              ],
              isShowingDistribution
            ).map(link => {
              const { label, onClick, to, href, target } = link;
              if (onClick) {
                return (
                  <li key={label} className={styles.linkListItem}>
                    <Pressable
                      key={label}
                      onPress={e => {
                        onClick(e);
                        onClickClose();
                      }}
                    >
                      {() => (
                        <Text size="lg" isBold color="#c9cbcd">
                          {label}
                        </Text>
                      )}
                    </Pressable>
                  </li>
                );
              }
              return (
                <li key={label} className={styles.linkListItem}>
                  <Link
                    to={to}
                    href={href}
                    key={label}
                    onClick={!target ? onClickClose : () => {}}
                    target={target}
                    className={cxLinkStyles({
                      link: true,
                      linkActive: pathname === (to || href),
                      linkAccount: true,
                    })}
                  >
                    {label}
                  </Link>
                </li>
              );
            })}
          </ul>
        ) : (
          <div className={styles.buttonContainer}>
            <a
              className={`${styles.signUpButton} ${LinkStyles.link}`}
              href="/signup"
              onClick={() => clickedNavLinkGoogleAnalytics('Sign Up')}
            >
              Sign up
            </a>
            <a
              href="/login"
              className={`${styles.loginLink} ${cxLinkStyles({
                link: true,
                linkActive: pathname === '/login',
              })}`}
              onClick={() => clickedNavLinkGoogleAnalytics('Log In')}
            >
              Log in
            </a>
          </div>
        )}
      </div>
    </div>
  </div>
);

MobileNav.defaultProps = {
  onLogOut: () => {},
  onClickClose: () => {},
  pathname: null,
};

MobileNav.propTypes = {
  links: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      href: PropTypes.string,
      to: PropTypes.string,
      isShowingNotificationBadge: PropTypes.bool,
      notificationBadge: PropTypes.string,
    })
  ).isRequired,
  isSignedIn: PropTypes.bool.isRequired,
  onLogOut: PropTypes.func,
  onClickClose: PropTypes.func,
  pathname: PropTypes.string,
};

export { MobileNav };
